import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePublicSite } from "@/contexts/PublicSiteContext";

const DEFAULT_SETTINGS = {
  brand_name: "Eudemonie",
  brand_tagline: "Sophrologie Caycédienne",
  nav_accompagnements_label: "Accompagnements",
  nav_sophrologie_label: "La Sophrologie",
  nav_methode_label: "La méthode",
  nav_developpement_label: "Développement\npersonnel",
  nav_blog_label: "Articles & Conseils",
  nav_tarifs_label: "Séances &\nTarifs",
  nav_a_propos_label: "À Propos",
  nav_contact_label: "Contact",
  nav_cta_label: "Prendre RDV",
};

const PAGE_LABEL_KEYS = {
  accompagnements: "nav_accompagnements_label",
  "sophrologie-caycedienne": "nav_sophrologie_label",
  "methode-coaching": "nav_methode_label",
  "methode-zenitude": "nav_methode_label",
  "demarche-massage": "nav_methode_label",
  "methode-prepm": "nav_methode_label",
  "developpement-personnel": "nav_developpement_label",
  "meditation-respiration": "nav_developpement_label",
  "motifs-consultation": "nav_developpement_label",
  blog: "nav_blog_label",
  "seances-tarifs": "nav_tarifs_label",
  "a-propos": "nav_a_propos_label",
  contact: "nav_contact_label",
  interventions: "nav_accompagnements_label",
};

function splitLabel(label) {
  return String(label || "").split("\n").filter(Boolean);
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings: publicSettings, manifest } = usePublicSite();
  const settings = { ...DEFAULT_SETTINGS, ...publicSettings };
  const planPages = Array.isArray(manifest?.pages) ? manifest.pages : [];
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const links = planPages
    .filter(page => page.enabled !== false && page.navigation === "main")
    .sort((a, b) => a.order - b.order)
    .map(page => ({ href: page.route, label: settings[PAGE_LABEL_KEYS[page.id]] || page.label }));
  const ctaPage = planPages.find(page => page.enabled !== false && page.navigation === "cta");

  return (
    <header data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-bone-light/95 backdrop-blur-md shadow-sm border-b border-sage/10" : "bg-transparent"}`}>
      <div className="mx-auto w-full max-w-[1500px] px-5 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex shrink-0 flex-col leading-none group">
            <span className="font-playfair text-xl text-charcoal tracking-tight group-hover:text-sage transition-colors duration-200">{settings.brand_name || DEFAULT_SETTINGS.brand_name}</span>
            <span className="font-manrope text-[11px] text-charcoal-muted tracking-widest uppercase whitespace-nowrap">{settings.brand_tagline || DEFAULT_SETTINGS.brand_tagline}</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex flex-1 items-start justify-center gap-0.5 min-w-0 pt-1" data-testid="desktop-nav">
            {links.map(l => (
              <Link key={l.href} to={l.href} className={`px-2 lg:px-2.5 py-2 rounded-lg text-sm font-manrope text-charcoal-muted hover:text-charcoal hover:bg-sage/10 transition-all duration-200 ${location.pathname === l.href ? "text-sage font-medium" : ""} ${splitLabel(l.label).length > 1 ? "flex flex-col items-center leading-tight" : "whitespace-nowrap leading-tight"}`}>
                <span className="whitespace-nowrap text-center">{splitLabel(l.label).map(line => <span key={line} className="block">{line}</span>)}</span>
              </Link>
            ))}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex shrink-0 items-center gap-3">
            {ctaPage && <Link to={ctaPage.route} data-testid="nav-cta" className="hidden md:flex btn-primary whitespace-nowrap text-sm px-5 py-2.5">
              {settings.nav_cta_label || DEFAULT_SETTINGS.nav_cta_label}
            </Link>}
            <button data-testid="mobile-menu-toggle" className="md:hidden p-2 text-charcoal hover:text-sage transition-colors" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
            className="md:hidden bg-bone-light/98 backdrop-blur-md border-b border-sage/10 px-6 pb-6 pt-2" data-testid="mobile-menu">
            {links.map(l => (
              <Link key={l.href} to={l.href} className={`block py-3 text-sm font-manrope border-b border-sage/10 last:border-0 ${location.pathname === l.href ? "text-sage font-medium" : "text-charcoal"}`}>
                <span className="block">{splitLabel(l.label).join(" ")}</span>
              </Link>
            ))}
            {ctaPage && <Link to={ctaPage.route} className="btn-primary mt-4 text-center block">{settings.nav_cta_label || DEFAULT_SETTINGS.nav_cta_label}</Link>}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
